import { FC, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { fetchBrandSeries } from "@/store/scores/slice";
import { addCompetitor } from "../../store/brands/slice";
import {
  selectBrandState,
  selectActiveCompetitorsSortedByWeeklyScoresState,
  selectBrandLoading,
  selectBrandLoadError,
} from "../../store/selectors/brands";
import { fetchBrands } from "../../store/brands/slice";
import CardBrandWrapper from "../../components/CardBrandWrapper/CardBrandWrapper";
import { DayScores } from "../../components/icons";
import BrandsLayout from "../../layout/BrandsLayout/BrandsLayout";
import {
  TitleWithFormRelatedBrands,
  TitleWithRightContent,
} from "../../components";
import Badge from "../../components/Badge";
import { AddFormItem } from "../../components/AddForm/types";
import { parseBrandsChartOpenIntent } from "./utils/brandsNavigation";
import { useScrollToBrandCard } from "../../hooks";

import styles from "./Brands.module.css";



const BRAND_EVERY_DAY_SERIES_DEFAULT_DAYS = 16;

const BrandsView: FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const loading = useSelector(selectBrandLoading);

  const loadError = useSelector(selectBrandLoadError);
  const brand = useSelector(selectBrandState);
  const competitors = useSelector(selectActiveCompetitorsSortedByWeeklyScoresState);

  const chartOpenIntent = useMemo(
    () => parseBrandsChartOpenIntent(location.hash),
    [location.hash],
  );

  const resolvedOpenBrandId = chartOpenIntent.shouldOpen
    ? chartOpenIntent.brandId
    : null;

  const isBrandCardsReady = !loading && resolvedOpenBrandId != null;

  useScrollToBrandCard(resolvedOpenBrandId, isBrandCardsReady, location.hash);

  useEffect(() => {
    dispatch(fetchBrandSeries({ days: BRAND_EVERY_DAY_SERIES_DEFAULT_DAYS }));
  }, [dispatch]);

  const handleAddCompetitor = ({ label, data }: AddFormItem) => {
    dispatch(addCompetitor({
      name: label,
      logoUrl: data?.logo_url,
      website: data?.domain ?? data?.website_url,
    }));
  };

  const renderTitleRightContent = () => {
    return <div className={styles["brands__title-columns"]}>
      <p><DayScores /> 7-day AI score</p>
      <div className={styles["brands__title-columns__names"]}>
        <p>Visibility</p>
        <p>Sentiment <Badge text="new" size="sm" className={styles["brands__title-columns__badge"]}/></p>
        <p>Ranking <Badge text="new" size="sm" className={styles["brands__title-columns__badge"]}/></p>
      </div>
    </div>;
  };

  return (
    <BrandsLayout>
      <TitleWithRightContent
        className={styles["brands__title"]}
        label="My Brand"
        rightContent={renderTitleRightContent()}
      />
      
      <CardBrandWrapper
        loading={loading}
        brands={brand}
        openBrandId={resolvedOpenBrandId}
        error={loadError}
        onRetry={() => dispatch(fetchBrands())}
      />

      <TitleWithFormRelatedBrands
        className={styles["brands__title"]}
        label="Competitors"
        onAddItem={handleAddCompetitor}
        placeholder="Add a new competitor"
      />

      <CardBrandWrapper
        loading={loading}
        brands={competitors}
        openBrandId={resolvedOpenBrandId}
        skeletonCount={3}
        error={loadError}
        onRetry={() => dispatch(fetchBrands())}
      />
      
    </BrandsLayout>
  );
};

export default BrandsView;
